import { useMutation, useQueryClient } from "react-query";

import { archiveTarget } from "api/targets";
import { useGlobalToast } from "components/GlobalToastProvider";
import { GET_GOAL_BY_ID, GET_LIST_OF_TARGETS, GET_TARGET_BY_ID } from "@constants/reactQueryKeys";

interface ArchiveTargetPayload {
  targetId: number;
  /** Parent goal id — required for invalidating the target list query. */
  goalId: number;
}

/**
 * Archive a target (silent action — no confirmation dialog per product spec).
 * Removes the target from the active list without losing session history.
 * Invalidates the target list and the target detail queries so the UI reflects
 * the archived state immediately.
 */
const useArchiveTarget = (options: any = {}) => {
  const { onSuccess = () => {}, onError = () => {}, ...otherOptions } = options;
  const queryClient = useQueryClient();
  const { showToast } = useGlobalToast();

  const {
    mutate: archiveTargetAction,
    isLoading: isArchivingTarget,
    ...rest
  } = useMutation(({ targetId }: ArchiveTargetPayload) => archiveTarget(targetId), {
    onSuccess: (response, variables) => {
      showToast({ message: "Target archived" });
      queryClient.invalidateQueries([GET_TARGET_BY_ID, variables.targetId]);
      queryClient.invalidateQueries([GET_LIST_OF_TARGETS, variables.goalId]);
      queryClient.invalidateQueries([GET_GOAL_BY_ID, variables.goalId]);
      onSuccess(response, variables);
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error || "Failed to archive target";
      showToast({ message: errorMessage, errorState: true });
      onError(error);
    },
    ...otherOptions,
  });

  return { archiveTargetAction, isArchivingTarget, ...rest };
};

export default useArchiveTarget;
